import React, { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { useMyAttendance } from 'core';

interface IMyLessonAttendance {
  lessonId: string;
  className?: string;
}

/**
 * Собственная отметка студента по этому занятию (ТЗ 0026).
 *
 * Студент видит ТОЛЬКО себя — список группы ему не показываем: это чужие
 * персональные данные и лишний повод спорить с преподавателем.
 *
 * Данные берутся из уже загруженного `myAttendance` по группе (он и так
 * per-lesson), поэтому дополнительного запроса здесь нет.
 *
 * Подсказки «отмечено автоматически при входе в 19:03» здесь НЕТ намеренно:
 * она читала поля source/markedAt, которых нет на задеплоенном бэкенде, и
 * из-за них у студента переставала открываться любая группа (см. комментарий
 * в core/operations/queries/attendance.ts). Вернуть вместе с деплоем 0026.
 */
export const MyLessonAttendance = memo(({ lessonId, className }: IMyLessonAttendance) => {
  const { t } = useTranslation();
  // groupId берём из маршрута, а не пропсом: `LessonComponent` его не знает,
  // и тащить проп через три уровня ради одной строки не стоит.
  const { groupId } = useParams();
  const { attendance } = useMyAttendance(groupId || '');

  const row = useMemo(
    () => attendance.rows.find((item) => String(item.lessonId) === String(lessonId)),
    [attendance.rows, lessonId],
  );

  if (!row) {
    return null;
  }

  const state =
    // eslint-disable-next-line no-nested-ternary
    row.attendance === true ? 'present' : row.attendance === false ? 'absent' : 'unmarked';

  return (
    <Wrapper className={className}>
      <Label>{t('lesson.myAttendance.marked')}:</Label>
      <Value $state={state}>{t(`lesson.myAttendance.${state}`)}</Value>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const Label = styled.span`
  color: ${Colors.gray_dark};
`;

const Value = styled.span<{ $state: string }>`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
  /* Сигнальные цвета здесь — ТЕКСТ, а не рамка, поэтому берутся тёмные
     варианты: signal_green даёт на белом 2.98:1, а gray — 2.16 при норме 4.5.
     marker_green читается 7.36 в светлой теме и 6.93 в тёмной, gray_dark —
     5.24 и 8.93. Красный оставлен: 4.37 и 5.63, и другого красного в палитре
     нет (правило в context/constants/colors.ts). */
  color: ${({ $state }) => {
    if ($state === 'present') return Colors.marker_green;
    if ($state === 'absent') return Colors.signal_red;
    return Colors.gray_dark;
  }};
`;
